import { CodeConfigurationField } from '@dtx-company/inter-app/src/types/flowcode'
import { EmailOutlined } from '@mui/icons-material'
import { FC, useEffect, useRef } from 'react'
import { MESSAGE_MAX_LENGTH } from '../constants'
import { ScanDestinationFieldProps } from '../ScanDestination'
import { Stack, TextArea, TextField } from '@dtx-company/design-system/src'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import {
  selectBody,
  selectError,
  selectNormalizedScanDestination,
  selectSubject
} from '@app/code/src/machines/locg/locgSelectors'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { useOnChange } from '../hooks/useOnChange'
import { useScreenSizeBreakpoints } from '@app/common/src/hooks/useScreenSizeBreakpoints'
import { useSelector } from '@xstate/react'
import useTranslation from 'next-translate/useTranslation'

export const EmailField: FC<ScanDestinationFieldProps> = ({ size = 'large' }) => {
  const { t } = useTranslation('logged-out-generator')
  const { locgService } = useGlobalServices()
  const normalizedScanDestination = useSelector(locgService, selectNormalizedScanDestination)
  const subject = useSelector(locgService, selectSubject)
  const body = useSelector(locgService, selectBody)
  const recipientError = useSelector(
    locgService,
    selectError
  )(CodeConfigurationField.SCAN_DESTINATION)
  const subjectError = useSelector(locgService, selectError)(CodeConfigurationField.SUBJECT)
  const messageError = useSelector(locgService, selectError)(CodeConfigurationField.BODY)

  const recipientRef = useRef<HTMLInputElement>(null)
  const subjectRef = useRef<HTMLInputElement>(null)
  const messageRef = useRef<HTMLInputElement>(null)

  const { isMobile } = useScreenSizeBreakpoints()

  useEffect(() => {
    if (recipientError) {
      recipientRef.current?.scrollIntoView({
        block: isMobile ? 'start' : 'center',
        behavior: 'smooth'
      })
    } else if (subjectError) {
      subjectRef.current?.scrollIntoView({
        block: isMobile ? 'start' : 'center',
        behavior: 'smooth'
      })
    } else if (messageError) {
      messageRef.current?.scrollIntoView({
        block: isMobile ? 'start' : 'center',
        behavior: 'smooth'
      })
    }
  }, [isMobile, messageError, recipientError, subjectError])

  const onChange = useOnChange()

  return (
    <Stack gap={{ xs: 2, md: 4 }}>
      <TextField
        size={size}
        name={CodeConfigurationField.SCAN_DESTINATION}
        label={t('LoggedOutGenerator.ScanDestination.options.email.subcomponents.EmailInput.label')}
        hiddenLabel
        value={normalizedScanDestination}
        placeholder={t(
          'LoggedOutGenerator.ScanDestination.options.email.subcomponents.EmailInput.placeholder'
        )}
        type="email"
        startAdornment={<EmailOutlined sx={{ color: 'onSurface.secondary' }} />}
        onChange={onChange}
        onFocus={() =>
          fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputEmailRecipient_Focused')
        }
        onBlur={e => {
          fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputEmailRecipient_Blurred', {
            text: e.target.value
          })
        }}
        inputProps={{
          'data-heap-id': 'LoggedOutGenerator_ScanDestinationInputEmailRecipient',
          ref: recipientRef
        }}
        fullWidth
        error={!!recipientError}
        helperText={recipientError}
      />

      <TextField
        size={size}
        name={CodeConfigurationField.SUBJECT}
        label={t(
          'LoggedOutGenerator.ScanDestination.options.email.subcomponents.SubjectInput.label'
        )}
        hiddenLabel
        value={subject}
        placeholder={t(
          'LoggedOutGenerator.ScanDestination.options.email.subcomponents.SubjectInput.placeholder'
        )}
        onChange={onChange}
        onFocus={() =>
          fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputEmailSubject_Focused')
        }
        onBlur={e => {
          fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputEmailSubject_Blurred', {
            text: e.target.value
          })
        }}
        inputProps={{
          'data-heap-id': 'LoggedOutGenerator_ScanDestinationInputEmailSubject',
          ref: subjectRef
        }}
        fullWidth
        error={!!subjectError}
        helperText={subjectError}
      />

      <TextArea
        size="large"
        name={CodeConfigurationField.BODY}
        label={t(
          'LoggedOutGenerator.ScanDestination.options.email.subcomponents.MessageInput.label'
        )}
        hiddenLabel
        value={body}
        placeholder={t(
          'LoggedOutGenerator.ScanDestination.options.email.subcomponents.MessageInput.placeholder'
        )}
        onChange={onChange}
        onFocus={() =>
          fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputEmailMessage_Focused')
        }
        onBlur={e => {
          locgService.send({ type: 'NEXT' })
          fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputEmailMessage_Blurred', {
            text: e.target.value
          })
        }}
        inputProps={{
          'data-heap-id': 'LoggedOutGenerator_ScanDestinationInputEmailMessage',
          ref: messageRef
        }}
        helperText={
          messageError ||
          t(
            'LoggedOutGenerator.ScanDestination.options.email.subcomponents.MessageInput.helperText',
            { characterCount: body?.length ?? 0, characterLimit: MESSAGE_MAX_LENGTH }
          )
        }
        error={!!messageError}
      />
    </Stack>
  )
}
